/**
 * FollowUpTasksList — top-level list with category + status filters.
 *
 * Reads /v3/follow-up-tasks. Per-row actions render through navigation;
 * complete + update writes are admin-auth-gated (forwards bypass key from
 * sessionStorage like other Wave 13 patterns).
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { ClipboardList } from 'lucide-react';
import { useApiClient } from '@/contexts/ApiClientContext';
import type {
  FollowUpTask,
  FollowUpTaskCategory,
  FollowUpTaskStatus,
} from '@retail-ops/shared-types';

const CATEGORY_LABEL: Record<string, string> = {
  'foreign-ssn-followup': 'Foreign-SSN follow-up',
  'deceased-followup': 'Deceased customer follow-up',
  'cip-core-entry': 'Enter ID in Core Director',
  'disclosure-copies': 'Give the customer their copies',
  'remote-signature': 'Remote signature pending',
  'signature-outstanding': 'Signature outstanding',
};

const STATUS_OPTIONS: ReadonlyArray<{ value: FollowUpTaskStatus | 'all'; label: string }> = [
  { value: 'all', label: 'All' },
  { value: 'open', label: 'Open' },
  { value: 'in-progress', label: 'In Progress' },
  { value: 'completed', label: 'Completed' },
  { value: 'auto-resolved', label: 'Auto-resolved' },
];

export function FollowUpTasksList(): JSX.Element {
  const client = useApiClient();
  const [tasks, setTasks] = useState<ReadonlyArray<FollowUpTask>>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<FollowUpTaskStatus | 'all'>('all');
  const [categoryFilter, setCategoryFilter] = useState<string>('all');

  const refresh = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const resp = await client.retail.followUpTasks.list({
        status: statusFilter === 'all' ? undefined : statusFilter,
        category: categoryFilter === 'all' ? undefined : categoryFilter,
        top: 500,
      });
      setTasks(resp.tasks ?? []);
    } catch (ex) {
      setError(ex instanceof Error ? ex.message : 'Failed to load follow-up tasks.');
    } finally {
      setLoading(false);
    }
  }, [client, statusFilter, categoryFilter]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const grouped = useMemo(() => {
    const buckets: Record<string, FollowUpTask[]> = {};
    for (const t of tasks) (buckets[t.taskCategory] ??= []).push(t);
    return buckets;
  }, [tasks]);

  return (
    <div>
      <header
        style={{
          marginBottom: 'var(--sp-6)',
          paddingBottom: 'var(--sp-5)',
          borderBottom: '1px solid var(--surface-border)',
        }}
      >
        <h1 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: 'var(--sp-3)' }}>
          <ClipboardList size={24} /> Follow-up tasks
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--ink-600)' }}>
          Long-running per-Cif tickets — Foreign-SSN annual W-8BEN refresh, Deceased customer
          milestones (Day 30 / 45 / 60).
        </p>
      </header>

      <div
        style={{
          display: 'flex',
          gap: 'var(--sp-4)',
          alignItems: 'center',
          marginBottom: 'var(--sp-5)',
          flexWrap: 'wrap',
        }}
      >
        <label style={{ fontSize: 12, color: 'var(--ink-600)' }}>
          Status:
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as FollowUpTaskStatus | 'all')}
            style={{ marginLeft: 'var(--sp-2)', padding: 'var(--sp-2)' }}
          >
            {STATUS_OPTIONS.map((s) => (
              <option key={s.value} value={s.value}>
                {s.label}
              </option>
            ))}
          </select>
        </label>
        <label style={{ fontSize: 12, color: 'var(--ink-600)' }}>
          Category:
          <select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
            style={{ marginLeft: 'var(--sp-2)', padding: 'var(--sp-2)' }}
          >
            <option value="all">All categories</option>
            <option value="foreign-ssn-followup">Foreign-SSN follow-up</option>
            <option value="deceased-followup">Deceased follow-up</option>
          </select>
        </label>
      </div>

      {loading && <div style={{ padding: 'var(--sp-6)' }}>Loading follow-up tasks…</div>}
      {error && (
        <div
          role="alert"
          style={{
            padding: 'var(--sp-5)',
            background: 'var(--err-bg)',
            color: 'var(--err-text)',
            borderRadius: 'var(--r-md)',
          }}
        >
          {error}
        </div>
      )}

      {!loading && !error && tasks.length === 0 && (
        <div
          style={{
            padding: 'var(--sp-7)',
            textAlign: 'center',
            color: 'var(--ink-500)',
            background: 'var(--surface-card)',
            border: '1px dashed var(--surface-border)',
            borderRadius: 'var(--r-md)',
          }}
        >
          No follow-up tasks match the current filters.
        </div>
      )}

      {!loading && !error && tasks.length > 0 && (
        <div style={{ display: 'grid', gap: 'var(--sp-6)' }}>
          {Object.entries(grouped).map(([cat, list]) => (
            <section key={cat}>
              <h2 style={{ fontSize: 13, margin: '0 0 var(--sp-3) 0' }}>
                {CATEGORY_LABEL[cat] ?? cat} ({list.length})
              </h2>
              <div
                style={{
                  display: 'grid',
                  gap: 'var(--sp-3)',
                }}
              >
                {list.map((t) => (
                  <FollowUpTaskCard key={t.id} task={t} onChanged={refresh} />
                ))}
              </div>
            </section>
          ))}
        </div>
      )}
    </div>
  );
}

export function FollowUpTaskCard({ task, onChanged }: { task: FollowUpTask; onChanged?: () => void }): JSX.Element {
  const ageDays = Math.floor(
    (Date.now() - new Date(task.triggerDate).getTime()) / (24 * 60 * 60 * 1000),
  );
  return (
    <article
      style={{
        background: 'var(--surface-card)',
        border: '1px solid var(--surface-border)',
        borderRadius: 'var(--r-md)',
        padding: 'var(--sp-5)',
        display: 'grid',
        gridTemplateColumns: '1fr auto',
        gap: 'var(--sp-3)',
      }}
    >
      <div>
        <div style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'baseline' }}>
          <strong style={{ fontSize: 13 }}>
            {task.targetEntityType.toUpperCase()}: {task.targetEntityKey}
          </strong>
          <span
            style={{
              fontSize: 11,
              padding: '2px 8px',
              borderRadius: 'var(--r-full)',
              background: statusBg(task.status),
              color: statusFg(task.status),
            }}
          >
            {task.status}
          </span>
        </div>
        <p style={{ margin: '4px 0 0', fontSize: 12, color: 'var(--ink-600)' }}>
          {task.notes ?? '(no notes)'}
        </p>
        <div style={{ marginTop: 'var(--sp-2)', fontSize: 11, color: 'var(--ink-500)' }}>
          Officer: {task.assignedOfficerCode ?? '(unassigned)'} · Branch: {task.branch ?? '—'} ·
          Triggered: {ageDays}d ago
          {task.nextActionAt
            ? ` · Next: ${new Date(task.nextActionAt).toLocaleDateString()}`
            : ''}
        </div>
        {task.taskCategory === 'remote-signature' && (task.status === 'open' || task.status === 'in-progress') && (
          <RemoteSignatureActions task={task} onChanged={onChanged} />
        )}
        {task.taskCategory === 'signature-outstanding' && (task.status === 'open' || task.status === 'in-progress') && (
          <SignatureOutstandingActions task={task} onChanged={onChanged} />
        )}
      </div>
    </article>
  );
}

// ── BACKLOG-1673 — nudge / resend / cancel for a remote signer's task ──────────────
interface RemoteSignaturePayload {
  presentationId?: string;
  signerIndex?: number;
  signerName?: string;
  email?: string;
  expiresAt?: string;
  nudges?: number;
  resends?: number;
}

function readRemotePayload(json: string | null | undefined): RemoteSignaturePayload {
  if (!json) return {};
  try { return JSON.parse(json) as RemoteSignaturePayload; } catch { return {}; }
}

export function RemoteSignatureActions({ task, onChanged }: { task: FollowUpTask; onChanged?: () => void }): JSX.Element {
  const client = useApiClient();
  const p = readRemotePayload(task.payloadJson);
  const [busy, setBusy] = useState<'nudge' | 'resend' | 'cancel' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [newCode, setNewCode] = useState<string | null>(null);
  const run = async (kind: 'nudge' | 'resend' | 'cancel'): Promise<void> => {
    if (!p.presentationId || busy) return;
    if (kind === 'cancel' && !window.confirm(`Cancel the remote signing request for ${p.signerName ?? 'this signer'}? The document stays unsigned and this task closes.`)) return;
    setBusy(kind); setMsg(null); setNewCode(null);
    try {
      if (kind === 'nudge') {
        const r = await client.retail.signing.remote.nudge(p.presentationId);
        setMsg(r.success ? (r.emailSent ? 'Reminder sent.' : `Reminder not emailed (${r.emailError ?? 'no address'}) — call the signer.`) : (r.error ?? 'Nudge failed.'));
      } else if (kind === 'resend') {
        const r = await client.retail.signing.remote.resend(p.presentationId);
        if (r.success) {
          setNewCode(r.accessCode ?? null);
          setMsg(r.emailSent ? 'New link e-mailed. The signer confirms their e-mail address and gets a one-time code by e-mail.' : `New link minted but not e-mailed (${r.emailError ?? 'no address'}). Copy the link and send it to the signer; the code still arrives by e-mail.`);
        } else setMsg(r.error ?? 'Resend failed.');
      } else {
        const r = await client.retail.signing.remote.cancel(p.presentationId, 'Cancelled by the officer from the follow-up task');
        setMsg(r.success ? 'Remote request cancelled. The document remains unsigned.' : (r.error ?? 'Cancel failed.'));
      }
      onChanged?.();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Action failed.');
    } finally {
      setBusy(null);
    }
  };
  return (
    <div style={{ marginTop: 'var(--sp-3)', display: 'grid', gap: 'var(--sp-2)' }}>
      <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
        <strong>{p.signerName ?? 'Signer'}</strong>{p.email ? ` · ${p.email}` : ''}
        {p.expiresAt ? ` · link expires ${new Date(p.expiresAt).toLocaleDateString()}` : ''}
        {(p.nudges ?? 0) > 0 ? ` · nudged ${p.nudges}×` : ''}{(p.resends ?? 0) > 0 ? ` · resent ${p.resends}×` : ''}
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap' }}>
        <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void run('nudge')}>{busy === 'nudge' ? 'Sending…' : 'Nudge'}</button>
        <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void run('resend')}>{busy === 'resend' ? 'Resending…' : 'Resend link'}</button>
        <button type="button" className="btn btn--sm btn--danger" disabled={!!busy} onClick={() => void run('cancel')}>{busy === 'cancel' ? 'Cancelling…' : 'Cancel request'}</button>
      </div>
      {msg && <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>{msg}{newCode ? <> <code style={{ fontFamily: 'var(--font-mono)', fontSize: 14, marginLeft: 6 }}>{newCode}</code></> : null}</div>}
    </div>
  );
}

interface SignatureOutstandingPayloadShape {
  signerIndex?: number;
  signerName?: string;
  role?: string;
  cif?: string | null;
  email?: string | null;
  documentsMissing?: ReadonlyArray<string>;
}

function readOutstandingPayload(json: string | null | undefined): SignatureOutstandingPayloadShape {
  if (!json) return {};
  try { return JSON.parse(json) as SignatureOutstandingPayloadShape; } catch { return {}; }
}

/**
 * Bill 2026-10-02 — a required signer who has neither signed nor been sent a
 * remote link when the account opening finished. Three ways out: sign in branch
 * (re-opens the ceremony on the application), send a remote link (becomes a
 * remote-signature task with nudge / resend / cancel), or close it deliberately.
 */
export function SignatureOutstandingActions({
  task, onChanged, onSignInBranch,
}: { task: FollowUpTask; onChanged?: () => void; onSignInBranch?: () => void }): JSX.Element {
  const client = useApiClient();
  const navigate = useNavigate();
  const p = readOutstandingPayload(task.payloadJson);
  const cartId = task.targetEntityKey;
  const [email, setEmail] = useState<string>(p.email ?? '');
  const [busy, setBusy] = useState<'send' | 'close' | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);

  const signInBranch = (): void => {
    if (onSignInBranch) { onSignInBranch(); return; }
    navigate(`/rao/${encodeURIComponent(cartId)}`, { state: { resumeSigning: true } });
  };

  const sendRemote = async (): Promise<void> => {
    if (busy || typeof p.signerIndex !== 'number') return;
    const to = email.trim();
    if (!to) { setMsg('Enter the signer\u2019s email address to send a remote link.'); return; }
    setBusy('send'); setMsg(null); setCode(null);
    try {
      const r = await client.retail.signing.remote.invite({
        cartId,
        signerIndex: p.signerIndex,
        signerName: p.signerName ?? 'Signer',
        ...(p.cif ? { signerCif: p.cif } : {}),
        email: to,
      });
      if (r.success) {
        setCode(r.accessCode ?? null);
        setMsg(r.emailSent
          ? 'Remote link e-mailed. The signer confirms their e-mail address and gets a one-time code by e-mail.'
          : `Link minted but not e-mailed (${r.emailError ?? 'no address'}). Copy the link and send it to the signer; the code still arrives by e-mail.`);
        onChanged?.();
      } else {
        setMsg(r.error ?? 'Could not send the remote link.');
      }
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Could not send the remote link.');
    } finally {
      setBusy(null);
    }
  };

  const close = async (): Promise<void> => {
    if (busy) return;
    if (!window.confirm(`Close this without a signature from ${p.signerName ?? 'this signer'}? The document stays unsigned and the task closes.`)) return;
    setBusy('close'); setMsg(null);
    try {
      // Empty bypass key rides the Teams bearer token (role 'Officer') — same as the customer-copy task.
      const bypassKey = (import.meta.env.VITE_ADMIN_BYPASS_KEY as string | undefined) ?? '';
      const resp = await client.retail.followUpTasks.complete(bypassKey, task.id, {
        completionReason: 'Officer closed the signature follow-up without a signature',
      });
      if (!resp.success) { setMsg(resp.error ?? 'Could not close the task.'); return; }
      setMsg('Closed. The document remains unsigned.');
      onChanged?.();
    } catch (err) {
      setMsg(err instanceof Error ? err.message : 'Could not close the task.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <div style={{ marginTop: 'var(--sp-3)', display: 'grid', gap: 'var(--sp-2)' }}>
      <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
        <strong>{p.signerName ?? 'Signer'}</strong>{p.role ? ` · ${p.role}` : ''}
        {p.documentsMissing && p.documentsMissing.length > 0 ? ` · unsigned: ${p.documentsMissing.join(', ')}` : ''}
      </div>
      <div style={{ display: 'flex', gap: 'var(--sp-3)', flexWrap: 'wrap', alignItems: 'center' }}>
        <button type="button" className="btn btn--sm btn--primary" disabled={!!busy} onClick={signInBranch}>Sign in branch</button>
        <input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.currentTarget.value)}
          placeholder="signer@example.com"
          aria-label="Signer email for the remote link"
          style={{ fontSize: 12, padding: '4px 8px', minWidth: 200 }}
          disabled={!!busy}
        />
        <button type="button" className="btn btn--sm" disabled={!!busy} onClick={() => void sendRemote()}>{busy === 'send' ? 'Sending…' : 'Send remote link'}</button>
        <button type="button" className="btn btn--sm btn--danger" disabled={!!busy} onClick={() => void close()}>{busy === 'close' ? 'Closing…' : 'Close without signature'}</button>
      </div>
      {msg && <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>{msg}{code ? <> <code style={{ fontFamily: 'var(--font-mono)', fontSize: 14, marginLeft: 6 }}>{code}</code></> : null}</div>}
    </div>
  );
}

function statusBg(s: string): string {
  switch (s) {
    case 'open':
      return 'var(--info-bg)';
    case 'in-progress':
      return 'var(--warn-bg)';
    case 'completed':
    case 'auto-resolved':
      return 'var(--ok-bg)';
    default:
      return 'var(--ink-100)';
  }
}

function statusFg(s: string): string {
  switch (s) {
    case 'open':
      return 'var(--info-text)';
    case 'in-progress':
      return 'var(--warn-text)';
    case 'completed':
    case 'auto-resolved':
      return 'var(--ok-text)';
    default:
      return 'var(--ink-700)';
  }
}
